import { ApiError } from '../api/client';
import { startStripeCheckout, type StripeCheckoutInput } from '../api/endpoints/stripe';
import { cardStepCopy } from '../lib/planPayment';
import { effectiveCycle, planHasAnnualBilling, type BillingCycle, type PlanTier } from '../lib/plan';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { ErrorBanner } from './ErrorBanner';
import { LockGlyph } from './PagoMethods';
import '../styles/pago.css';

/**
 * The card step: the last step of signup, and what a locked CRM shows until
 * Stripe confirms a card. One button to Stripe Checkout for the plan on file
 * (a subscription for Base, Pro, Max or Ultra). Dale only takes a card with a
 * payment, so Independientes, which pays per booking, gets no button.
 * Stripe sends the owner back to /pago?stripe=listo, which waits for the
 * confirmation. Dale never sees the card.
 */

export type CardStepProps = {
  plan: PlanTier;
  cycle: BillingCycle;
  /** The monthly price on file when known (a discount agreed with Dale); the list price otherwise. */
  monthlyPriceCents?: number;
  /** On the free trial, the trial end as a date: saving the card is optional and charges nothing until then. */
  freeUntil?: string;
};

const GUARANTEE_TEXT = 'Garantía de 30 días: si Dale no es para ti, te devolvemos todo.';
const FALLBACK_ERROR = 'No pudimos abrir el pago con tarjeta. Intenta de nuevo en unos minutos.';

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error && error.message ? error.message : FALLBACK_ERROR;
}

export function CardStep(props: CardStepProps) {
  return planHasAnnualBilling(props.plan) ? <PaidCardStep {...props} /> : <NoCardStep />;
}

function NoCardStep() {
  return (
    <section className="card-step" aria-labelledby="card-step-title">
      <h2 id="card-step-title" className="card-step-title">
        Sin tarjeta
      </h2>
      <p className="card-step-lede">El plan Reservas no paga mensualidad, así que no necesitas tarjeta. Pagas solo por las reservas que recibas.</p>
    </section>
  );
}

function PaidCardStep({ plan, cycle, monthlyPriceCents, freeUntil }: CardStepProps) {
  const start = useMutationAttempt({
    mutationFn: (input: StripeCheckoutInput, key: string) => startStripeCheckout(input, key),
  });
  const copy = cardStepCopy({
    plan,
    cycle,
    ...(monthlyPriceCents !== undefined ? { monthlyPriceCents } : {}),
    ...(freeUntil ? { freeUntil } : {}),
  });

  return (
    <section className="card-step" aria-labelledby="card-step-title">
      <h2 id="card-step-title" className="card-step-title">
        {freeUntil ? 'Guarda tu tarjeta' : 'Agrega tu tarjeta'}
      </h2>
      <p className="card-step-lede">{copy.lede}</p>
      {start.error ? <ErrorBanner message={errorText(start.error)} /> : null}
      <button
        type="button"
        className="btn"
        disabled={start.isPending}
        onClick={() => start.mutate({ plan, cycle: effectiveCycle(plan, cycle) })}
      >
        {start.isPending ? 'Abriendo Stripe…' : copy.action}
      </button>
      <p className="pago-trust">
        <LockGlyph />
        {freeUntil
          ? 'La guardas en la página segura de Stripe. Dale no ve ni guarda los datos de tu tarjeta.'
          : 'Pagas en la página segura de Stripe. Dale no ve ni guarda los datos de tu tarjeta.'}
      </p>
      <p className="pago-trust">{GUARANTEE_TEXT}</p>
    </section>
  );
}
